import React from 'react';
import CustomCells from 'ui/structure/CustomTableCells/CustomCells';
import noop from 'utils/noop';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './TransactionReport.module.css';

import download from 'assets/img/download.png';

export interface IColumn {
  Header: string;
  accessor: string;
  width: number;
}

const defaultColumns = [
  {
    Header: 'Schedule ID',
    accessor: 'scheduleId',
    width: 120,
  },
  {
    Header: 'Source Account',
    accessor: 'sourceAccount',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccount, accountType },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccount}</span>
          <span className={styles.subTitle}>{accountType}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Source Account Name',
    accessor: 'sourceAccountName',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccountName },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccountName}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Beneficiary',
    accessor: 'beneficiary',
    width: 120,
    Cell: ({
      row: {
        original: { beneficiaryAccount, beneficiaryName },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{beneficiaryAccount}</span>
          <span className={styles.subTitle}>{beneficiaryName}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Beneficiary Bank',
    accessor: 'beneficiaryBank',
    width: 120,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: 120,
    Cell: ({
      row: {
        original: { amount },
      },
    }) => (
      <CustomCells>
        <span>{`GHS ${Number(amount).toFixed(2)}`}</span>
      </CustomCells>
    ),
  },
  {
    Header: 'Total Amount',
    accessor: 'totalAmount',
    width: 120,
    Cell: ({
      row: {
        original: { amount },
      },
    }) => (
      <CustomCells>
        <span>{`GHS ${Number(amount).toFixed(2)}`}</span>
      </CustomCells>
    ),
  },
  {
    Header: 'Narration',
    accessor: 'narration',
    width: 120,
  },
  {
    Header: 'Status',
    accessor: 'status',
    width: 120,
  },
  {
    Header: 'Created',
    accessor: 'created',
    width: 120,
    Cell: ({
      row: {
        original: { createdBy, created },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{createdBy}</span>
          <span className={styles.subTitle}>{created}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Final Approval',
    accessor: 'finalApproval',
    width: 120,
    Cell: ({
      row: {
        original: { approvedBy, finalApproval },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{approvedBy}</span>
          <span className={styles.subTitle}>{finalApproval}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Advice',
    accessor: 'advice',
    width: 60,
    Cell: () => (
      <div title="Download advice" onKeyPress={noop} role="button" className={styles.pointer} tabIndex={0}>
        <img src={download} alt="delete icon" />
      </div>
    ),
  },
];

const bulkTransferListColumns = [
  {
    Header: 'Beneficiary Name',
    accessor: 'beneficiaryName',
    width: 120,
  },
  {
    Header: 'Beneficiary Account',
    accessor: 'beneficiaryAccount',
    width: 100,
  },
  {
    Header: 'BeneficiaryBank',
    accessor: 'beneficiaryBank',
    width: 120,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: 120,
  },
  {
    Header: 'Transaction Type',
    accessor: 'transactionType',
    width: 120,
  },
  {
    Header: 'Description',
    accessor: 'narration',
    width: 120,
  },
  {
    Header: 'Status',
    accessor: 'status',
    width: 120,
  },
  {
    Header: 'Advice',
    accessor: 'advice',
    width: 60,
    Cell: () => (
      <div onKeyPress={noop} role="button" tabIndex={0} className={styles.pointer}>
        <img src={download} alt="delete icon" />
      </div>
    ),
  },
];

const prepaidCardColumns = [
  {
    Header: 'Customer ID',
    accessor: 'beneficiaryAccount',
    width: 120,
  },
  {
    Header: 'Card Number',
    accessor: 'lastDigits',
    width: 120,
    Cell: ({
      row: {
        original: { lastDigits },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>************{lastDigits}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Card Name',
    accessor: 'cardName',
    width: 120,
  },
  {
    Header: 'Schedule ID',
    accessor: 'scheduleId',
    width: 120,
  },
  {
    Header: 'Source Account',
    accessor: 'sourceAccount',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccount, accountType },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccount}</span>
          <span className={styles.subTitle}>{accountType}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Phone Number',
    accessor: 'phoneNumber',
    width: 120,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: 120,
  },
  {
    Header: 'Narration',
    accessor: 'narration',
    width: 120,
  },
  {
    Header: 'Status',
    accessor: 'status',
    width: 120,
  },
  {
    Header: 'Created',
    accessor: 'created',
    width: 120,
    Cell: ({
      row: {
        original: { createdBy, created, createDate },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{createdBy}</span>
          <span className={styles.subTitle}>{created || createDate}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Final Approval',
    accessor: 'finalApproval',
    width: 120,
    Cell: ({
      row: {
        original: { approvedBy, approveBy, finalApproval, finalApproveDate },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{approvedBy || approveBy}</span>
          <span className={styles.subTitle}>{finalApproval || finalApproveDate}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Advice',
    accessor: 'advice',
    width: 60,
    Cell: () => (
      <div onKeyPress={noop} role="button" tabIndex={0} className={styles.pointer}>
        <img src={download} alt="download icon" />
      </div>
    ),
  },
];

const bulkColumns = [
  {
    Header: 'Bulk ID',
    accessor: 'bulkId',
    width: 120,
    Cell: ({
      row: {
        original: { bulkId },
      },
    }) => (
      <CustomCells>
        <div className={styles.pointer} onKeyPress={noop} role="button" tabIndex={0}>
          <span className={styles.bulkId}>{bulkId}</span>
        </div>
      </CustomCells>
    ),
  },
  // {
  //   Header: 'Schedule ID',
  //   accessor: 'scheduleId',
  //   width: 120,
  // },
  {
    Header: 'Source Account',
    accessor: 'sourceAccount',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccount, accountType },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccount}</span>
          <span className={styles.subTitle}>{accountType}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Total Beneficiaries',
    accessor: 'totalCount',
    width: 120,
  },
  {
    Header: 'Charges',
    accessor: 'charge',
    width: 120,
  },
  {
    Header: 'Total Amount',
    accessor: 'totalAmount',
    width: 120,
  },
  {
    Header: 'Status',
    accessor: 'workFlowStatus',
    width: 120,
  },
  {
    Header: 'Created',
    accessor: 'created',
    width: 120,
    Cell: ({
      row: {
        original: { initiator, dateInitiated, createdBy, createDate },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{initiator || createdBy}</span>
          <span className={styles.subTitle}>{dateInitiated || createDate}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Final Approval',
    accessor: 'finalApproval',
    width: 120,
    Cell: ({
      row: {
        original: { approveBy, dateApproved, finalApproveDate },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{approveBy}</span>
          <span className={styles.subTitle}>{dateApproved || finalApproveDate}</span>
        </div>
      </CustomCells>
    ),
  },
  // {
  //   Header: 'Advice',
  //   accessor: 'advice',
  //   width: 60,
  //   Cell: () => (
  //     <div onKeyPress={noop} role="button" tabIndex={0} className={styles.pointer}>
  //       <img src={download} alt="delete icon" />
  //     </div>
  //   ),
  // },
];

const mobileMoneyColumns = [
  {
    Header: 'Bulk ID',
    accessor: 'bulkId',
    width: 120,
    Cell: ({
      row: {
        original: { bulkId },
      },
    }) => (
      <CustomCells>
        <div className={styles.pointer} onKeyPress={noop} role="button" tabIndex={0}>
          <span className={styles.bulkId}>{bulkId}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Schedule ID',
    accessor: 'scheduleId',
    width: 120,
    Cell: ({
      row: {
        original: { scheduleId },
      },
    }) => (
      <CustomCells>
        <div className={styles.pointer} onKeyPress={noop} role="button" tabIndex={0}>
          <span className={styles.bulkId}>{scheduleId}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Source Account',
    accessor: 'sourceAccount',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccount, accountType },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccount}</span>
          <span className={styles.subTitle}>{accountType}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Beneficiary',
    accessor: 'beneficiaryName',
    width: 120,
  },
  {
    Header: 'Total Beneficiaries',
    accessor: 'totalCount',
    width: 120,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: 120,
    Cell: ({
      row: {
        original: { amount },
      },
    }) => (
      <CustomCells>
        <span>{`GHS ${Number(amount).toFixed(2)}`}</span>
      </CustomCells>
    ),
  },
  {
    Header: 'Charges',
    accessor: 'charge',
    width: 120,
  },
  {
    Header: 'E-levy',
    accessor: 'elevy',
    width: 120,
  },
  {
    Header: 'Network',
    accessor: 'txnMobileMoneyCode',
    width: 120,
  },
  {
    Header: 'Wallet',
    accessor: 'beneficiaryAccount',
    width: 100,
    Cell: ({
      row: {
        original: { beneficiaryAccount },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.subTitle}>{beneficiaryAccount}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Total Amount',
    accessor: 'totalAmount',
    width: 120,
    Cell: ({
      row: {
        original: { amount, totalAmount },
      },
    }) => (
      <CustomCells>
        <span>{`GHS ${Number(amount || totalAmount).toFixed(2)}`}</span>
      </CustomCells>
    ),
  },
  {
    Header: 'Status',
    accessor: 'workFlowStatus',
    width: 120,
    Cell: ({
      row: {
        original: { status, workFlowStatus },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{status || workFlowStatus}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Created',
    accessor: 'created',
    width: 120,
    Cell: ({
      row: {
        original: { createdBy, created, createDate },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{createdBy}</span>
          <span className={styles.subTitle}>{created || createDate}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Final Approval',
    accessor: 'finalApproval',
    width: 120,
    Cell: ({
      row: {
        original: { approvedBy, approveBy, finalApproval, finalApproveDate },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{approvedBy || approveBy}</span>
          <span className={styles.subTitle}>{finalApproval || finalApproveDate}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Advice',
    accessor: 'advice',
    width: 60,
    Cell: () => (
      <div onKeyPress={noop} role="button" tabIndex={0} className={styles.pointer}>
        <img src={download} alt="delete icon" />
      </div>
    ),
  },
];

const billsColumn = [
  {
    Header: 'Schedule ID',
    accessor: 'scheduleId',
    width: 120,
    Cell: ({
      row: {
        original: { scheduleId },
      },
    }) => (
      <CustomCells>
        <div className={styles.pointer} onKeyPress={noop} role="button" tabIndex={0}>
          <span className={styles.bulkId}>{scheduleId}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Source Account',
    accessor: 'sourceAccount',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccount, accountType },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccount}</span>
          <span className={styles.subTitle}>{accountType}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Invoice Number',
    accessor: 'invoiceNumber',
    width: 120,
  },
  {
    Header: 'Expiry Date',
    accessor: 'expiryDate',
    width: 120,
  },
  // {
  //   Header: 'Advice Number',
  //   accessor: 'invoiceNumber',
  //   width: 120,
  // },
  {
    Header: 'Contribution Type',
    accessor: 'contributionType',
    width: 120,
  },
  {
    Header: 'Service',
    accessor: 'service',
    width: 120,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: 120,
  },
  {
    Header: 'Status',
    accessor: 'status',
    width: 120,
  },
  {
    Header: 'Created',
    accessor: 'created',
    width: 120,
    Cell: ({
      row: {
        original: { createdBy, created },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{createdBy}</span>
          <span className={styles.subTitle}>{created}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Final Approval',
    accessor: 'finalApproval',
    width: 120,
    Cell: ({
      row: {
        original: { approvedBy, finalApproval },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{approvedBy}</span>
          <span className={styles.subTitle}>{finalApproval}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Advice',
    accessor: 'advice',
    width: 60,
    Cell: () => (
      <div onKeyPress={noop} role="button" tabIndex={0} className={styles.pointer}>
        <img src={download} alt="delete icon" />
      </div>
    ),
  },
];

const fxColumn = [
  {
    Header: 'Request ID',
    accessor: 'requestId',
    width: 120,
    Cell: ({
      row: {
        original: { requestId },
      },
    }) => (
      <CustomCells>
        <div className={styles.pointer} onKeyPress={noop} role="button" tabIndex={0}>
          <span className={styles.bulkId}>{requestId}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Transfer Type',
    accessor: 'transferType',
    width: 120,
  },
  {
    Header: 'Source Account',
    accessor: 'sourceAccount',
    width: 100,
    Cell: ({
      row: {
        original: { sourceAccount, accountType },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{sourceAccount}</span>
          <span className={styles.subTitle}>{accountType}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Beneficiary',
    accessor: 'beneficiary',
    width: 120,
    Cell: ({
      row: {
        original: { beneficiaryAccount, beneficiaryName },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{beneficiaryAccount}</span>
          <span className={styles.subTitle}>{beneficiaryName}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Beneficiary Country',
    accessor: 'beneficiaryCountry',
    width: 120,
  },
  {
    Header: 'Swift Code',
    accessor: 'swiftCode',
    width: 120,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: 120,
  },
  {
    Header: 'Status',
    accessor: 'status',
    width: 120,
  },
  {
    Header: 'Created',
    accessor: 'created',
    width: 120,
    Cell: ({
      row: {
        original: { initiator, dateInitiated },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{initiator}</span>
          <span className={styles.subTitle}>{dateInitiated}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Final Approval',
    accessor: 'finalApproval',
    width: 120,
    Cell: ({
      row: {
        original: { approver, dateApproved },
      },
    }) => (
      <CustomCells>
        <div className={styles.cellContainer}>
          <span className={styles.title}>{approver}</span>
          <span className={styles.subTitle}>{dateApproved}</span>
        </div>
      </CustomCells>
    ),
  },
  {
    Header: 'Advice',
    accessor: 'advice',
    width: 60,
    Cell: () => (
      <div onKeyPress={noop} role="button" tabIndex={0} className={styles.pointer}>
        <img src={download} alt="delete icon" />
      </div>
    ),
  },
];

const allColumns = {
  defaultColumn: defaultColumns,
  bulk: bulkColumns,
  mobileMoney: mobileMoneyColumns,
  prepaidCard: prepaidCardColumns,
  billsColumn,
  fxColumn,
  bulkTransferListColumns,
};

export default allColumns;
